/**
 * <frequently-bought-together-loader>
 * Loads recommendations, recalculates bundle totals instantly, and
 * submits through CartManager so cart UI stays synchronized.
 */
(function () {
  if (customElements.get('frequently-bought-together-loader')) return;

  class FrequentlyBoughtTogetherLoader extends HTMLElement {
    connectedCallback() {
      this.slot = this.querySelector('[data-fbt-slot]');
      this.summary = this.querySelector('[data-fbt-summary]');
      this.totalEl = this.querySelector('[data-fbt-total-price]');
      this.originalEl = this.querySelector('[data-fbt-original-price]');
      this.savingsEl = this.querySelector('[data-fbt-savings]');
      this.countEl = this.querySelector('[data-fbt-count]');
      this.addAllBtn = this.querySelector('[data-fbt-add-all]');
      this.mainVariantId = Number(this.dataset.mainVariantId) || 0;
      this.mainPrice = Number(this.dataset.mainPrice) || 0;
      this.discountPercent = Number(this.dataset.bundleDiscountPercent || 10);
      this._isAdding = false;
      this._lastTotal = Number(this.mainPrice) || 0;
      this._onChange = (event) => {
        if (event.target.matches('.fbt-card__checkbox, [data-fbt-variant-select], .fbt-card select, [data-fbt-quantity], .fbt-card input[type="number"]')) {
          this.syncRecommendationVariant(event.target);
          this.recalculate();
        }
      };
      this._onInput = (event) => {
        if (event.target.matches('[data-fbt-quantity], .fbt-card input[type="number"]')) this.recalculate();
      };
      this._onMainQuantityChange = () => this.recalculate();

      this.addAllBtn?.addEventListener('click', () => this.addAll());
      this.bindProductEvents();
      this.load();
    }

    disconnectedCallback() {
      this.slot?.removeEventListener('change', this._onChange);
      this.slot?.removeEventListener('input', this._onInput);
      this.mainQuantityInput?.removeEventListener('change', this._onMainQuantityChange);
      this.mainQuantityInput?.removeEventListener('input', this._onMainQuantityChange);
      this._variantUnsubscriber?.();
      this._cartUnsubscriber?.();
    }

    bindProductEvents() {
      this.productInfo = this.closest('product-info');
      this.productSectionId =
        this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section || this.dataset.sectionId;
      this.mainQuantityInput = this.productInfo?.querySelector('.product-form__quantity .quantity__input');

      this.mainQuantityInput?.addEventListener('change', this._onMainQuantityChange);
      this.mainQuantityInput?.addEventListener('input', this._onMainQuantityChange);

      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this._variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (this.productSectionId && data?.sectionId !== this.productSectionId) return;
          this.updateMainVariant(data?.variant);
        });
      }

      this._cartUnsubscriber = window.CartManager?.on('cart:updated', () => this.recalculate());
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error('FBT fetch failed');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('.fbt');

        if (!fragment) {
          this.remove();
          return;
        }

        this.slot.innerHTML = '';
        this.slot.appendChild(fragment);
        this.summary.hidden = false;
        this.slot.addEventListener('change', this._onChange);
        this.slot.addEventListener('input', this._onInput);
        this.recalculate();
      } catch (error) {
        console.error('[FBT] could not load recommendations', error);
        this.remove();
      }
    }

    getCheckedItems() {
      return Array.from(this.slot.querySelectorAll('.fbt-card__checkbox:checked'));
    }

    recalculate() {
      const selectedAddOns = this.getSelectedAddOns();
      const mainQuantity = this.getMainQuantity();
      const mainTotal = this.mainPrice * mainQuantity;
      const addOnsTotal = selectedAddOns.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const originalTotal = mainTotal + addOnsTotal;
      const eligibleForSavings = selectedAddOns.length > 0;
      const savings = eligibleForSavings ? Math.round((originalTotal * this.discountPercent) / 100) : 0;
      const bundleTotal = Math.max(originalTotal - savings, 0);
      const itemCount = mainQuantity + selectedAddOns.reduce((sum, item) => sum + item.quantity, 0);
      const fmt = (cents) => this.formatMoney(cents);

      if (this.originalEl) this.originalEl.textContent = fmt(originalTotal);
      if (this.totalEl) {
        if (window.LuxuryAnimations && Number.isFinite(this._lastTotal)) {
          window.LuxuryAnimations.animateMoneyElement(this.totalEl, this._lastTotal, bundleTotal);
        } else {
          this.totalEl.textContent = fmt(bundleTotal);
        }
      }
      if (this.savingsEl) {
        this.savingsEl.textContent = savings > 0 ? `Save ${fmt(savings)}` : 'Select an item to save';
        this.savingsEl.classList.toggle('is-active', savings > 0);
      }
      if (this.countEl) this.countEl.textContent = `${itemCount} item${itemCount > 1 ? 's' : ''}`;
      if (this.addAllBtn) this.addAllBtn.disabled = this._isAdding || !this.mainVariantId;
      this._lastTotal = bundleTotal;
    }

    async addAll() {
      if (this._isAdding) return;
      this._isAdding = true;
      this.setLoading(true);

      const bundleProperty = { _bundle: 'Frequently Bought Together' };
      const items = [{ id: this.mainVariantId, quantity: this.getMainQuantity(), properties: bundleProperty }];

      this.getSelectedAddOns().forEach((item) => {
        if (item.variantId) items.push({ id: item.variantId, quantity: item.quantity, properties: bundleProperty });
      });

      try {
        await window.CartManager.addItems(items);
        window.LuxuryAnimations?.pulse(this.summary, 'fbt-summary--added');
      } catch (error) {
        console.error('[FBT] add all failed', error);
      } finally {
        this._isAdding = false;
        this.setLoading(false);
        this.recalculate();
      }
    }

    setLoading(isLoading) {
      if (!this.addAllBtn) return;
      this.addAllBtn.disabled = isLoading || !this.mainVariantId;
      this.addAllBtn.classList.toggle('is-loading', isLoading);
      this.addAllBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    getSelectedAddOns() {
      return this.getCheckedItems()
        .map((element) => ({
          element,
          variantId: Number(element.dataset.variantId),
          price: Number(element.dataset.price || 0),
          quantity: this.getRecommendationQuantity(element),
        }))
        .filter((item) => item.variantId && item.quantity > 0);
    }

    getMainQuantity() {
      return this.normalizeQuantity(Number(this.mainQuantityInput?.value || this.dataset.mainQuantity || 1));
    }

    getRecommendationQuantity(checkbox) {
      const card = checkbox.closest('.fbt-card');
      const input = card?.querySelector('[data-fbt-quantity], input[type="number"]');
      return this.normalizeQuantity(Number(input?.value || input?.dataset.quantity || 1));
    }

    normalizeQuantity(value) {
      return Math.max(1, Number.isFinite(value) ? Math.floor(value) : 1);
    }

    syncRecommendationVariant(target) {
      const select = target.matches('[data-fbt-variant-select], .fbt-card select') ? target : null;
      if (!select) return;

      const selectedOption = select.selectedOptions?.[0];
      const checkbox = select.closest('.fbt-card')?.querySelector('.fbt-card__checkbox');
      if (!selectedOption || !checkbox) return;

      const variantId = Number(selectedOption.value || selectedOption.dataset.variantId);
      const price = Number(selectedOption.dataset.price);

      if (variantId) checkbox.dataset.variantId = String(variantId);
      if (Number.isFinite(price)) {
        checkbox.dataset.price = String(price);
        const priceEl = select.closest('.fbt-card')?.querySelector('.fbt-card__price');
        if (priceEl) priceEl.textContent = this.formatMoney(price);
      }
    }

    updateMainVariant(variant) {
      if (!variant) {
        this.mainVariantId = 0;
        this.mainPrice = 0;
      } else {
        this.mainVariantId = Number(variant.id);
        this.mainPrice = Number(variant.price || 0);
      }

      this.dataset.mainVariantId = String(this.mainVariantId || '');
      this.dataset.mainPrice = String(this.mainPrice || 0);
      this.recalculate();
    }

    formatMoney(cents) {
      return window.RewardManager?.formatMoney(cents) || window.LuxuryCartUtils?.formatMoney(cents) || `${cents / 100}`;
    }
  }

  customElements.define('frequently-bought-together-loader', FrequentlyBoughtTogetherLoader);
})();
